import React from 'react'
import type { BadgeDefinition } from '@ppwr/convex/badges/catalog'
import { BadgePlaceholder, type BadgeArtProps } from './BadgePlaceholder'
import { ProfileCompleteBadge } from './ProfileCompleteBadge'
import { LoungeMemberBadge } from './LoungeMemberBadge'
import { LoungeGamesPlayedBadge } from './LoungeGamesPlayedBadge'
import { GamesFinishedBadge } from './GamesFinishedBadge'
import { HandsPlayedBadge } from './HandsPlayedBadge'
import { HandsWonBadge } from './HandsWonBadge'
import { ShowdownWinsBadge } from './ShowdownWinsBadge'
import { NoShowdownWinsBadge } from './NoShowdownWinsBadge'
import { AllinWinsBadge } from './AllinWinsBadge'
import { BiggestPotWinBadge } from './BiggestPotWinBadge'
import { BluffCatchesBluffBadge } from './BluffCatchesBluffBadge'
import { PreflopFoldsBadge } from './PreflopFoldsBadge'
import { RankStraightBadge } from './RankStraightBadge'
import { RankFlushBadge } from './RankFlushBadge'
import { RankFullHouseBadge } from './RankFullHouseBadge'
import { RankFourOfAKindBadge } from './RankFourOfAKindBadge'
import { RankStraightFlushBadge } from './RankStraightFlushBadge'
import { RankRoyalFlushBadge } from './RankRoyalFlushBadge'
import { ChipsWonBadge } from './ChipsWonBadge'
import { Daily10StreakBadge } from './Daily10StreakBadge'
import { Daily10PerfectBadge } from './Daily10PerfectBadge'
import { Daily10PlayedBadge } from './Daily10PlayedBadge'
import { CthPerfectBadge } from './CthPerfectBadge'
import { CthSpeedrunBadge } from './CthSpeedrunBadge'
import { CthStreakBadge } from './CthStreakBadge'
import { CthPlayedBadge } from './CthPlayedBadge'
import { PpPerfectBadge } from './PpPerfectBadge'
import { PpSpeedrunBadge } from './PpSpeedrunBadge'
import { PpStreakBadge } from './PpStreakBadge'
import { PpPlayedBadge } from './PpPlayedBadge'
import { PlayStreakBadge } from './PlayStreakBadge'
import { DaysPlayedBadge } from './DaysPlayedBadge'
import { NightOwlBadge } from './NightOwlBadge'
import { EarlyBirdBadge } from './EarlyBirdBadge'

/**
 * Badge art registry, keyed by the catalog's explicit assetKey. Every
 * designer asset is converted by tools/scripts/convert-badge.mjs; anything
 * missing still renders the labeled placeholder safely.
 */
const badgeArt: Partial<Record<string, React.ComponentType<BadgeArtProps>>> = {
  profile_complete: ProfileCompleteBadge,
  lounge_member: LoungeMemberBadge,
  lounge_games_played: LoungeGamesPlayedBadge,
  games_finished: GamesFinishedBadge,
  hands_played: HandsPlayedBadge,
  hands_won: HandsWonBadge,
  showdown_wins: ShowdownWinsBadge,
  no_showdown_wins: NoShowdownWinsBadge,
  allin_wins: AllinWinsBadge,
  biggest_pot_win: BiggestPotWinBadge,
  bluff_catches_bluff: BluffCatchesBluffBadge,
  preflop_folds: PreflopFoldsBadge,
  rank_straight: RankStraightBadge,
  rank_flush: RankFlushBadge,
  rank_full_house: RankFullHouseBadge,
  rank_four_of_a_kind: RankFourOfAKindBadge,
  rank_straight_flush: RankStraightFlushBadge,
  rank_royal_flush: RankRoyalFlushBadge,
  chips_won: ChipsWonBadge,
  daily10_streak: Daily10StreakBadge,
  daily10_perfect: Daily10PerfectBadge,
  daily10_played: Daily10PlayedBadge,
  cth_perfect: CthPerfectBadge,
  cth_speedrun: CthSpeedrunBadge,
  cth_streak: CthStreakBadge,
  cth_played: CthPlayedBadge,
  pp_perfect: PpPerfectBadge,
  pp_speedrun: PpSpeedrunBadge,
  pp_streak: PpStreakBadge,
  pp_played: PpPlayedBadge,
  play_streak: PlayStreakBadge,
  days_played: DaysPlayedBadge,
  night_owl: NightOwlBadge,
  early_bird: EarlyBirdBadge
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

interface BadgeArtComponentProps extends BadgeArtProps {
  badge: BadgeDefinition
}

/** Single entry point: renders the badge's art, or the labeled placeholder. */
export const BadgeArt: React.FC<BadgeArtComponentProps> = ({ badge, ...props }) => {
  const Art = badgeArt[badge.assetKey]
  if (Art) return <Art {...props} />
  return <BadgePlaceholder {...props} label={initials(badge.name)} />
}
